/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridSection from "../../widgets/GridSection";
import GridDropdown from "../../widgets/GridDropdown";
import GridTextfield from "../../widgets/GridTextfield";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateClientInfo } from "../../../states/slcPersonalInformation.js";

/*============================================================
  PROPRIETARY - DATA MODELS
============================================================*/
import { createPersonalInfo } from "./PersonalInfoModel.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { createPayload, createReduxAction } from "../../../utils/modelHelper.js";

const employmentStatusData = [
  { key: "FULLTIME", value: "Full Time" },
  { key: "PARTTIME", value: "Part Time" },
  { key: "SELFEMPLOYED", value: "Self Employed" },
  { key: "RETIRED", value: "Retired" },
  { key: "OTHERS", value: "Others" },
];

const highestQualificationData = [
  { key: "PRIMARY", value: "Primary" },
  { key: "SECONDARY", value: "Secondary" },
  { key: "NOLEVELS", value: "N/O Levels" },
  { key: "DIPLOMA", value: "Post Sec/A levels/Diploma" },
  { key: "TERTIARY", value: "Degree and Above" },
];

const emptydata = [];

export default function SectionEmploymentAndEducation() {
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const [employmentStattus, setEmploymentStatus] = useState(personalInfo.employmentstatus);
  const [occupation, setOccupation] = useState(personalInfo.occupation);
  const [employer, setEmployer] = useState(personalInfo.employer);
  const [highestQualitifcation, setHighestQualification] = useState(personalInfo.highesteducation);
  const [isFinancialBusiness, setIsFinancialBusiness] = useState(personalInfo.isFinancialBusiness);

  const [employmentStattusError, setEmploymentStatusError] = useState("");
  const [employerError, setEmployerError] = useState("");
  const [occupationError, setOccupationError] = useState("");
  const [highestQualitifcationError, setHighestQualificationError] = useState("");
  const [isFinancialBusinessError, setIsFinancialBusinessError] = useState("");

  const dispatch = useDispatch();

  //REFRESH COMPONENT WITH CURRENT APPLICANTE DATA
  useEffect(() => {
    setEmploymentStatus(personalInfo.employmentstatus);
    setEmployer(personalInfo.employer);
    setOccupation(personalInfo.occupation);
    setHighestQualification(personalInfo.highesteducation);
    setIsFinancialBusiness(personalInfo.isFinancialBusiness);
  }, [currApplicant]);

  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    const personalInfo = createPersonalInfo();

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "employmentstatus") {
        personalInfo.employmentstatus = e.target.value;
        setEmploymentStatus(e.target.value);
        actionType = "UPDATE_EMPLOYMENT_STATUS";
      }

      if (id === "highestqualification") {
        personalInfo.highesteducation = e.target.value;
        setHighestQualification(e.target.value);
        actionType = "UPDATE_HIGHEST_QUALIFICATION";
      }
    } else {
      let type = "";
      let name = "";

      id = e.target.id;

      if (id === "") {
        type = e.target.type;
        name = e.target.name;
      }

      if (id === "occupation") {
        if (e.target.value.length === 0) setOccupationError("Occupation is required.");
        else if (e.target.value.length > 30) setOccupationError("Occupation exceeded 30 characters");
        else setOccupationError("");

        personalInfo.occupation = e.target.value;
        setOccupation(e.target.value);
        actionType = "UPDATE_OCCUPATION";
      }

      if (id === "employer") {
        if (e.target.value.length === 0) setEmployerError("Employer is required.");
        else if (e.target.value.length > 30) setEmployerError("Employer exceeded 30 characters");
        else setEmployerError("");

        personalInfo.employer = e.target.value;
        setEmployer(e.target.value);
        actionType = "UPDATE_EMPLOYER";
      }

      if (type === "radio" && name === "emp-business-nature") {
        personalInfo.isFinancialBusiness = e.target.value;
        setIsFinancialBusiness(e.target.value);
        actionType = "UPDATE_IS_FINANCIAL_BUSINESS";
      }
    }

    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));
  }

  return (
    <Grid container spacing={2}>
      <GridSectionHeader sectionName="Employment & Education" />

      {/* EMPLOYMENT STATUS */}
      <GridDropdown
        id="employmentstatus"
        key="employmentstatus"
        columnSize="6"
        label="Employment Status"
        currState={employmentStattus}
        onChange={handleChange}
        data={employmentStatusData}
      />

      {/* OCCUPATION */}
      <GridTextfield
        id="occupation"
        key="occupation"
        columnSize="6"
        label="Occupation"
        currState={occupation}
        onChange={handleChange}
      />

      {/* EMPLOYER */}
      <GridTextfield
        id="employer"
        key="employer"
        columnSize="6"
        label="Employer"
        currState={employer}
        onChange={handleChange}
      />

      {/* HIGHEST QUALIFICATION */}
      <GridDropdown
        id="highestqualification"
        key="highestqualification"
        columnSize="6"
        label="Highest Qualification"
        currState={highestQualitifcation}
        onChange={handleChange}
        data={highestQualificationData}
      />

      {/* DECLARATION - OCCUPAION NATURE*/}
      <GridSection columnSize="12">
        <Typography variant="body1" sx={{ pb: 2 }}>
          Is your occupation involved in any of the nature of business listed below?
        </Typography>

        <List disablePadding sx={{ pl: 2, listStyleType: "disc" }}>
          <ListItem disablePadding sx={{ display: "list-item" }}>
            Dealers in Precious Metals or stones
          </ListItem>
          <ListItem disablePadding sx={{ display: "list-item" }}>
            Petroleum Industry
          </ListItem>
          <ListItem disablePadding sx={{ display: "list-item" }}>
            Money Services Business (excludes Banks)
          </ListItem>
          <ListItem disablePadding sx={{ display: "list-item" }}>
            Casino or Other Types of Gaming Operators
          </ListItem>
          <ListItem disablePadding sx={{ display: "list-item" }}>
            Virtual / Digital Currencies
          </ListItem>
        </List>

        {/* <FormControl> */}
        <RadioGroup
          row
          sx={{ pt: 1 }}
          name="emp-business-nature"
          defaultValue=""
          value={isFinancialBusiness}
          onChange={handleChange}
        >
          <FormControlLabel id="idyes" value="YES" control={<Radio />} label="Yes" />
          <FormControlLabel id="idno" value="NO" control={<Radio />} label="No" />
        </RadioGroup>
        {/* </FormControl> */}
      </GridSection>
    </Grid>
  );
}
